import React, {
    useState,
    useRef,
    useCallback,
    useMemo,
    useEffect
} from 'react';
import { Box, IconButton, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import {
    useGetChatProfileQuery,
    useGetChatUserQuery,
    useGetChatMessagesQuery,
    useReadChatMessageMutation
} from '@store/feature/chat/chatApi';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import OpenInFullIcon from '@mui/icons-material/OpenInFull';
import TopBar from './TopBar';
import { FaArrowDown } from 'react-icons/fa';
import MessageBox from '../MessageBox';
import SendChat from './SendChat';
import { Link, useLocation } from 'react-router-dom';
import { GAS_PATH, ELECTRICITY_PATH, COMMON_PATH } from '@utils';

const PAGE_SIZE = 50;

const SingleChat = ({
    handleGoBack,
    handleToggleShowChat,
    navigationStack
}) => {
    const location = useLocation();
    const id = navigationStack[navigationStack.length - 1];

    // API hooks
    const { data: profile } = useGetChatProfileQuery();
    const { data: userProfile, isLoading, error } = useGetChatUserQuery(id);

    // Pagination state
    const [before, setBefore] = useState(null);
    const [allMessages, setAllMessages] = useState([]);
    const [isLoadingOlderMessages, setIsLoadingOlderMessages] = useState(false);
    const [hasMoreMessages, setHasMoreMessages] = useState(true);

    // Scroll state
    const [showScrollButton, setShowScrollButton] = useState(false);
    const bottomRef = useRef(null);
    const messagesWrapperRef = useRef(null);
    const topMessageRef = useRef(null);

    // Control flags
    const hasInitiallyScrolled = useRef(false);
    const shouldScrollToBottom = useRef(false);
    const isNewChatSession = useRef(false);
    const isPaginationLocked = useRef(false);
    const paginationJustCompleted = useRef(false);
    const suppressAutoScrollUntilRef = useRef(0);
    const wasAtBottomRef = useRef(true); // Track if user was at bottom before message update

    // API for paginated messages
    const { data: page, refetch } = useGetChatMessagesQuery(
        {
            userId: id,
            before: before,
            limit: PAGE_SIZE
        },
        {
            refetchOnMountOrArgChange: true,
            pollingInterval: 3000
        }
    );

    // Track previous 'after' value to detect new messages during polling
    const prevAfterRef = useRef(page?.after);
    useEffect(() => {
        if (
            page?.after &&
            prevAfterRef.current &&
            page.after !== prevAfterRef.current
        ) {
            setBefore(null);
        }
        prevAfterRef.current = page?.after;
    }, [page?.after]);

    // Read message mutation
    const [readMessage] = useReadChatMessageMutation();

    // Reset flags on chat change
    useEffect(() => {
        hasInitiallyScrolled.current = false;
        shouldScrollToBottom.current = false;
        isNewChatSession.current = true;
        isPaginationLocked.current = false;
        paginationJustCompleted.current = false;
        setShowScrollButton(false);
    }, [id]);

    // Reset chat state when id changes
    const prevIdRef = useRef(id);
    useEffect(() => {
        if (prevIdRef.current && prevIdRef.current !== id) {
            setAllMessages([]);
            setBefore(null);
            setIsLoadingOlderMessages(false);
            setHasMoreMessages(true);
            hasInitiallyScrolled.current = false;
            shouldScrollToBottom.current = false;
            isNewChatSession.current = true;
            isPaginationLocked.current = false;
            refetch();
        }
        prevIdRef.current = id;
    }, [id, refetch]);

    const isAtBottom = useCallback(() => {
        if (!messagesWrapperRef.current) return true;
        const { scrollTop, scrollHeight, clientHeight } =
            messagesWrapperRef.current;
        return scrollHeight - scrollTop - clientHeight <= 50;
    }, []);

    // Merge new messages
    useEffect(() => {
        // Store if user was at bottom before updating messages
        wasAtBottomRef.current = isAtBottom();

        if (!page?.items || !Array.isArray(page.items)) {
            setIsLoadingOlderMessages(false);
            isPaginationLocked.current = false;
            return;
        }
        if (page.items.length === 0) {
            setIsLoadingOlderMessages(false);
            setHasMoreMessages(false);
            isPaginationLocked.current = false;
            return;
        }
        const ascending = [...page.items].reverse();
        const hasMore = page.items.length === PAGE_SIZE;
        setHasMoreMessages(hasMore);

        if (before === null) {
            setAllMessages((prev) => {
                const existingIds = new Set(prev?.map((msg) => msg.uuid));
                const newUniqueMessages = ascending.filter(
                    (msg) => !existingIds.has(msg.uuid)
                );
                const filteredPrev = prev.filter((msg) => {
                    if (msg.uuid && msg.uuid.startsWith('temp-')) {
                        return !newUniqueMessages.some(
                            (nm) =>
                                nm.body === msg.body &&
                                nm.from === msg.from &&
                                nm.to === msg.to &&
                                JSON.stringify(nm.files) ===
                                    JSON.stringify(msg.files)
                        );
                    }
                    return !(
                        msg.status === 'sent' &&
                        msg.from === profile?.uuid &&
                        newUniqueMessages.some((nm) => nm.uuid === msg.uuid)
                    );
                });
                if (newUniqueMessages.length === 0) {
                    if (!isLoadingOlderMessages)
                        setIsLoadingOlderMessages(false);
                    return filteredPrev;
                }
                return [...filteredPrev, ...newUniqueMessages];
            });
            setIsLoadingOlderMessages(false);
            isPaginationLocked.current = false;
        } else {
            // Overlay: keep scroll position stable when loading older messages
            const wrapper = messagesWrapperRef.current;
            let prevScrollHeight = wrapper ? wrapper.scrollHeight : 0;
            setAllMessages((prev) => {
                const existingIds = new Set(prev?.map((msg) => msg.uuid));
                const newUniqueMessages = ascending.filter(
                    (msg) => !existingIds.has(msg.uuid)
                );
                if (newUniqueMessages.length === 0) return prev;
                return [...newUniqueMessages, ...prev];
            });
            setTimeout(() => {
                if (wrapper) {
                    // Maintain scroll position after prepending older messages
                    wrapper.scrollTop =
                        wrapper.scrollHeight -
                        prevScrollHeight +
                        wrapper.scrollTop;
                }
                setIsLoadingOlderMessages(false);
                isPaginationLocked.current = false;
            }, 100);
        }
    }, [
        page?.items,
        before,
        page?.after,
        page?.total_count,
        profile?.uuid,
        isLoadingOlderMessages,
        isAtBottom
    ]);

    // Unified auto-scroll logic
    const scrollToBottom = useCallback((behavior = 'smooth') => {
        if (bottomRef.current) {
            bottomRef.current.scrollIntoView({ behavior });
        }
    }, []);

    useEffect(() => {
        if (!allMessages.length) return;
        if (Date.now() < suppressAutoScrollUntilRef.current) return;

        //  Scroll if user was at bottom before new messages arrived
        if (wasAtBottomRef.current) {
            setTimeout(() => scrollToBottom('smooth'), 80);
            wasAtBottomRef.current = false; // Reset after scroll
            return;
        }

        //  If user is already at bottom, force scroll on new message
        if (isAtBottom()) {
            setTimeout(() => scrollToBottom('smooth'), 80);
            return;
        }

        if (
            isPaginationLocked.current ||
            isLoadingOlderMessages ||
            before !== null ||
            paginationJustCompleted.current
        ) {
            return;
        }
        const lastMessage = allMessages[allMessages.length - 1];
        const isOwnMessage = lastMessage?.from === profile?.uuid;
        if (isOwnMessage) {
            setTimeout(() => scrollToBottom('smooth'), 100);
            return;
        }
        if (isNewChatSession.current && !hasInitiallyScrolled.current) {
            const performInitialScroll = () => {
                if (!bottomRef.current || !messagesWrapperRef.current)
                    return false;
                const { scrollHeight, clientHeight } =
                    messagesWrapperRef.current;
                if (scrollHeight <= clientHeight) return false;
                bottomRef.current.scrollIntoView({ behavior: 'auto' });
                setTimeout(() => {
                    const { scrollTop, scrollHeight, clientHeight } =
                        messagesWrapperRef.current;
                    const atBottom =
                        scrollHeight - scrollTop - clientHeight <= 10;
                    if (!atBottom) {
                        bottomRef.current.scrollIntoView({
                            behavior: 'smooth'
                        });
                    }
                }, 100);
                return true;
            };
            let attempts = 0;
            const maxAttempts = 5;
            const tryScroll = () => {
                attempts++;
                const success = performInitialScroll();
                if (!success && attempts < maxAttempts) {
                    setTimeout(tryScroll, 200 * attempts);
                } else {
                    hasInitiallyScrolled.current = true;
                    setTimeout(() => {
                        isNewChatSession.current = false;
                    }, 500);
                }
            };
            requestAnimationFrame(() => {
                setTimeout(tryScroll, 100);
            });
            return;
        }
        if (!hasInitiallyScrolled.current) {
            requestAnimationFrame(() => {
                setTimeout(() => {
                    if (bottomRef.current && messagesWrapperRef.current) {
                        const { scrollHeight, clientHeight } =
                            messagesWrapperRef.current;
                        if (scrollHeight > clientHeight) {
                            bottomRef.current.scrollIntoView({
                                behavior: 'auto'
                            });
                            setTimeout(() => {
                                bottomRef.current.scrollIntoView({
                                    behavior: 'smooth'
                                });
                                hasInitiallyScrolled.current = true;
                            }, 200);
                        }
                    }
                }, 150);
            });
            return;
        }
        if (shouldScrollToBottom.current) {
            setTimeout(() => {
                scrollToBottom('smooth');
                shouldScrollToBottom.current = false;
            }, 100);
            return;
        }
        setTimeout(() => {
            if (isAtBottom() && !paginationJustCompleted.current) {
                scrollToBottom('smooth');
            }
        }, 50);
    }, [
        allMessages,
        scrollToBottom,
        isAtBottom,
        profile?.uuid,
        isLoadingOlderMessages,
        before
    ]);

    // Show/hide scroll button
    const handleScroll = useCallback(() => {
        const atBottom = isAtBottom();
        setShowScrollButton(!atBottom && allMessages.length > 0);
    }, [isAtBottom, allMessages.length]);
    useEffect(() => {
        const wrapper = messagesWrapperRef.current;
        if (!wrapper) return;
        wrapper.addEventListener('scroll', handleScroll);
        return () => wrapper.removeEventListener('scroll', handleScroll);
    }, [handleScroll]);

    // Infinite scroll for older messages
    useEffect(() => {
        if (
            !allMessages.length ||
            !topMessageRef.current ||
            isLoadingOlderMessages ||
            !hasMoreMessages
        ) {
            return;
        }
        const observer = new IntersectionObserver(
            (entries) => {
                if (
                    entries[0].isIntersecting &&
                    hasMoreMessages &&
                    !isLoadingOlderMessages
                ) {
                    isPaginationLocked.current = true;
                    suppressAutoScrollUntilRef.current = Date.now() + 2000;
                    setIsLoadingOlderMessages(true);
                    const oldestMessage = allMessages[0];
                    if (oldestMessage) {
                        setBefore(oldestMessage.created_at);
                    }
                }
            },
            {
                threshold: 0.1,
                rootMargin: '20px'
            }
        );
        const currentRef = topMessageRef.current;
        if (currentRef) observer.observe(currentRef);
        return () => {
            if (currentRef) observer.unobserve(currentRef);
            observer.disconnect();
        };
    }, [allMessages, hasMoreMessages, isLoadingOlderMessages]);

    // Mark messages as read
    useEffect(() => {
        if (id && profile?.uuid !== id && allMessages.length > 0) {
            const lastMessage = allMessages[allMessages.length - 1];
            if (
                lastMessage?.from === id &&
                ['sent', 'delivered'].includes(lastMessage.status)
            ) {
                readMessage(lastMessage.uuid);
            }
        }
    }, [id, profile, allMessages, readMessage]);

    // Handle sent messages
    const handleMessageSent = useCallback(
        (newMessage) => {
            if (!newMessage?.uuid) return;
            setAllMessages((prev) => {
                if (!prev.some((msg) => msg.uuid === newMessage.uuid)) {
                    const newMessages = [
                        ...prev,
                        {
                            ...newMessage,
                            from: profile?.uuid,
                            status: 'sent'
                        }
                    ];
                    requestAnimationFrame(() => {
                        if (bottomRef.current) {
                            bottomRef.current.scrollIntoView({
                                behavior: 'smooth'
                            });
                        }
                    });
                    return newMessages;
                }
                return prev;
            });
            setShowScrollButton(false);
            // Reset pagination to fetch latest messages
            setBefore(null);
            isPaginationLocked.current = false;
            paginationJustCompleted.current = false;
            suppressAutoScrollUntilRef.current = 0;
            setIsLoadingOlderMessages(false);
            setTimeout(() => {
                if (bottomRef.current) {
                    bottomRef.current.scrollIntoView({ behavior: 'smooth' });
                }
            }, 100);
            setTimeout(() => {
                if (bottomRef.current) {
                    bottomRef.current.scrollIntoView({ behavior: 'smooth' });
                }
            }, 300);
            shouldScrollToBottom.current = true;
            // Always refetch after sending a message
            refetch();
        },
        [profile?.uuid, refetch]
    );

    // Enhanced date formatting
    const formatDate = useCallback((timestampOrIso) => {
        const date =
            typeof timestampOrIso === 'number'
                ? new Date(timestampOrIso * 1000)
                : new Date(timestampOrIso);
        const today = new Date();
        const yesterday = new Date(today);
        yesterday.setDate(yesterday.getDate() - 1);
        const messageDate = new Date(
            date.getFullYear(),
            date.getMonth(),
            date.getDate()
        );
        const todayDate = new Date(
            today.getFullYear(),
            today.getMonth(),
            today.getDate()
        );
        const yesterdayDate = new Date(
            yesterday.getFullYear(),
            yesterday.getMonth(),
            yesterday.getDate()
        );
        if (messageDate.getTime() === todayDate.getTime()) {
            return 'Today';
        } else if (messageDate.getTime() === yesterdayDate.getTime()) {
            return 'Yesterday';
        } else {
            const diffTime = todayDate.getTime() - messageDate.getTime();
            const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
            if (diffDays <= 7 && diffDays > 0) {
                return date.toLocaleDateString('en-US', { weekday: 'long' });
            } else {
                return date.toLocaleDateString('en-US', {
                    month: 'short',
                    day: 'numeric',
                    year:
                        messageDate.getFullYear() !== todayDate.getFullYear()
                            ? 'numeric'
                            : undefined
                });
            }
        }
    }, []);

    // Memoized message list with date headers
    const messageList = useMemo(() => {
        return allMessages?.map((msg, i) => {
            const prev = allMessages[i - 1];
            const currentDate = new Date(msg.created_at).toDateString();
            const previousDate = prev
                ? new Date(prev.created_at).toDateString()
                : null;
            const showDateHeader = i === 0 || currentDate !== previousDate;
            return (
                <React.Fragment key={msg.uuid}>
                    {showDateHeader && (
                        <Box
                            sx={{
                                display: 'flex',
                                justifyContent: 'center',
                                my: 2,
                                width: '100%',
                                position: 'relative'
                            }}
                        >
                            <Box
                                sx={{
                                    backgroundColor: 'rgba(255,255,255,0.95)',
                                    backdropFilter: 'blur(8px)',
                                    borderRadius: '20px',
                                    px: 2,
                                    py: 0.5,
                                    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                                    border: '1px solid rgba(0,0,0,0.06)',
                                    minWidth: '60px',
                                    textAlign: 'center'
                                }}
                            >
                                <Typography
                                    variant="caption"
                                    sx={{
                                        color: '#374151',
                                        fontWeight: 600,
                                        fontSize: '0.75rem',
                                        textTransform: 'capitalize',
                                        letterSpacing: '0.5px'
                                    }}
                                >
                                    {formatDate(msg.created_at)}
                                </Typography>
                            </Box>
                        </Box>
                    )}
                    <MessageBox
                        id={msg.uuid}
                        messageText={msg.body}
                        timestamp={msg.created_at}
                        isRight={msg.from === profile?.uuid}
                        status={msg.status}
                        files={msg.files}
                    />
                </React.Fragment>
            );
        });
    }, [allMessages, formatDate, profile?.uuid]);

    // ...existing code for header...
    return (
        <Box
            sx={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column'
            }}
        >
            <Box
                sx={{
                    height: '50px',
                    backgroundColor: '#27485D',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '0 10px'
                }}
            >
                {/* ...existing code for header... */}
                <Box
                    sx={{
                        display: 'flex',
                        gap: '10px',
                        alignItems: 'center'
                    }}
                    onClick={handleGoBack}
                >
                    <IconButton sx={{ padding: '0', margin: '0' }}>
                        <ArrowBackIcon
                            sx={{ color: '#fff', fontSize: '1.5rem' }}
                        />
                    </IconButton>
                    <Typography
                        variant="body2"
                        sx={{ color: 'white', fontWeight: 'medium' }}
                    >
                        Go to Chats
                    </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                    <Box
                        component={Link}
                        to={`${
                            location.pathname.includes('gas')
                                ? `${GAS_PATH.CHAT}/${id}`
                                : location.pathname.includes('electricity')
                                ? `${ELECTRICITY_PATH.CHAT}/${id}`
                                : `${COMMON_PATH.CHAT}/${id}`
                        }`}
                    >
                        <IconButton>
                            <OpenInFullIcon
                                sx={{ color: '#fff', fontSize: '1.2rem' }}
                            />
                        </IconButton>
                    </Box>
                    <IconButton onClick={handleToggleShowChat}>
                        <CloseIcon sx={{ color: '#fff', fontSize: '1.5rem' }} />
                    </IconButton>
                </Box>
            </Box>
            <TopBar profile={userProfile?.user} />
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    flex: 1,
                    height: 'calc(100% - 100px)'
                }}
            >
                <Box
                    ref={messagesWrapperRef}
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        width: '100%',
                        px: '10px',
                        pt: '10px',
                        overflowY: 'auto',
                        height: 'calc(100% - 60px)',
                        '&::-webkit-scrollbar': { width: '8px' },
                        '&::-webkit-scrollbar-track': {
                            backgroundColor: '#f1f1f1',
                            borderRadius: '4px'
                        },
                        '&::-webkit-scrollbar-thumb': {
                            backgroundColor: '#c1c1c1',
                            borderRadius: '4px',
                            '&:hover': { backgroundColor: '#a1a1a1' }
                        },
                        scrollbarColor: '#c1c1c1 #f1f1f1'
                    }}
                >
                    {isLoadingOlderMessages && (
                        <Box
                            sx={{
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                py: 1,
                                gap: 1
                            }}
                        >
                            <span className="animate-spin text-[#2FACFE] text-lg">
                                ⏳
                            </span>
                            <Typography
                                variant="caption"
                                sx={{ color: 'teal', fontSize: '0.85rem' }}
                            >
                                Loading older messages...
                            </Typography>
                        </Box>
                    )}
                    <div ref={topMessageRef} />
                    {allMessages.length > 0 ? (
                        <>{messageList}</>
                    ) : (
                        <Box className="flex justify-center py-20">
                            <Typography>No messages available!</Typography>
                        </Box>
                    )}
                    <div ref={bottomRef} className="w-full h-[1px] mb-2" />
                </Box>
                {showScrollButton && (
                    <Box sx={{ position: 'relative', width: '100%' }}>
                        <Box
                            sx={{
                                position: 'absolute',
                                left: '50%',
                                bottom: '8px',
                                transform: 'translateX(-50%)',
                                zIndex: 1000
                            }}
                        >
                            <button
                                className="rounded-full flex justify-center items-center bg-[#2FACFE] w-8 h-8 shadow-lg hover:bg-[#1e8fd4] transition-colors"
                                onClick={() => {
                                    shouldScrollToBottom.current = true;
                                    scrollToBottom('smooth');
                                    setShowScrollButton(false);
                                }}
                            >
                                <FaArrowDown color="white" />
                            </button>
                        </Box>
                    </Box>
                )}
                <SendChat
                    id={userProfile?.user?.uuid}
                    userProfile={userProfile}
                    onMessageSent={handleMessageSent}
                />
            </Box>
        </Box>
    );
};

export default SingleChat;
